<script setup lang="ts">
  const { t, tmResolved } = useResolvedI18n()
  const localePath = useLocalePath()
  useHead({ title: () => t('education.title') })

  type ProjectItem = { id: string; name: string; level: string }
  type MentorRow = { stage: string; role: string; focus: string }
  type Badge = { title: string; desc: string }
  type PracticeStep = { title: string; body: string }

  const projectItems = computed(() => tmResolved<ProjectItem[]>('education.projects.items'))
  const mentorRows = computed(() => tmResolved<MentorRow[]>('education.mentor.rows'))
  const badges = computed(() => tmResolved<Badge[]>('education.badge.badges'))
  const practiceSteps = computed(() => tmResolved<PracticeStep[]>('education.practice.steps'))
</script>

<template>
  <section
    class="page-banner"
    style="
      background-image: url('https://www.sanyau.edu.cn/_upload/article/images/f6/0e/137ab3794a60b3a70cc482fa16f7/8bd441ba-1409-4008-a3d0-0cd4bd195456.jpg');
    "
  >
    <h2>{{ t('education.banner') }}</h2>
    <div class="breadcrumb">
      <NuxtLink :to="localePath('/')">{{ t('common.home') }}</NuxtLink>
      &gt; {{ t('education.breadcrumb') }}
    </div>
  </section>

  <section class="content-section">
    <div class="container">
      <div class="content-wrapper">
        <div class="content-main">
          <!-- PBL项目制培养 -->
          <div class="content-card" id="pbl">
            <h3>{{ t('education.pbl.title') }}</h3>
            <p>{{ t('education.pbl.intro1') }}</p>
            <p>{{ t('education.pbl.intro2') }}</p>

            <h4 style="font-size: 17px; color: var(--theme-blue); margin: 20px 0 10px">
              {{ t('education.pbl.mechanismTitle') }}
            </h4>
            <div class="highlight-box">
              <h4>{{ t('education.pbl.intakeTitle') }}</h4>
              <p>{{ t('education.pbl.intakeBody') }}</p>
            </div>
            <div class="highlight-box">
              <h4>{{ t('education.pbl.selectionTitle') }}</h4>
              <p>{{ t('education.pbl.selectionBody') }}</p>
            </div>

            <!-- <h4 style="font-size: 17px; color: var(--theme-blue); margin: 20px 0 10px">
              {{ t('education.pbl.levelsTitle') }}
            </h4>
            <div class="level-cards">
              <div class="level-card">
                <div class="level-card-header l1">
                  <h4>{{ t('education.pbl.l1Title') }}</h4>
                  <span>{{ t('education.pbl.l1Meta') }}</span>
                </div>
                <div class="level-card-body">
                  <p>{{ t('education.pbl.l1Body') }}</p>
                </div>
              </div>
              <div class="level-card">
                <div class="level-card-header l2">
                  <h4>{{ t('education.pbl.l2Title') }}</h4>
                  <span>{{ t('education.pbl.l2Meta') }}</span>
                </div>
                <div class="level-card-body">
                  <p>{{ t('education.pbl.l2Body') }}</p>
                </div>
              </div>
              <div class="level-card">
                <div class="level-card-header l3">
                  <h4>{{ t('education.pbl.l3Title') }}</h4>
                  <span>{{ t('education.pbl.l3Meta') }}</span>
                </div>
                <div class="level-card-body">
                  <p>{{ t('education.pbl.l3Body') }}</p>
                </div>
              </div>
            </div> -->
          </div>

          <!-- 项目池 -->
          <!-- <div class="content-card" id="projects">
            <h3>{{ t('education.projects.title') }}</h3>
            <p style="text-indent: 0">{{ t('education.projects.intro') }}</p>
            <table class="data-table">
              <thead>
                <tr>
                  <th>{{ t('education.projects.colId') }}</th>
                  <th>{{ t('education.projects.colName') }}</th>
                  <th>{{ t('education.projects.colLevel') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in projectItems" :key="i">
                  <td>{{ item.id }}</td>
                  <td>{{ item.name }}</td>
                  <td>{{ item.level }}</td>
                </tr>
              </tbody>
            </table>
            <p style="text-indent: 0; margin-top: 16px">
              <strong>{{ t('education.projects.flagshipLabel') }}</strong>
              {{ t('education.projects.flagship') }}
            </p>
          </div> -->

          <!-- 三导师制 -->
          <div class="content-card" id="mentor">
            <h3>{{ t('education.mentor.title') }}</h3>
            <p>{{ t('education.mentor.intro') }}</p>

            <div class="mentor-grid" style="margin-top: 20px">
              <div class="mentor-card">
                <h3>{{ t('education.mentor.academicTitle') }}</h3>
                <div class="mentor-title">{{ t('education.mentor.academicRole') }}</div>
                <p>{{ t('education.mentor.academicDesc') }}</p>
              </div>
              <div class="mentor-card">
                <h3>{{ t('education.mentor.industryTitle') }}</h3>
                <div class="mentor-title">{{ t('education.mentor.industryRole') }}</div>
                <p>{{ t('education.mentor.industryDesc') }}</p>
              </div>
              <div class="mentor-card">
                <h3>{{ t('education.mentor.aiTitle') }}</h3>
                <div class="mentor-title">{{ t('education.mentor.aiRole') }}</div>
                <p>{{ t('education.mentor.aiDesc') }}</p>
              </div>
            </div>

            <h4 style="font-size: 17px; color: var(--theme-blue); margin: 30px 0 10px">
              {{ t('education.mentor.progressionTitle') }}
            </h4>
            <p>{{ t('education.mentor.progressionIntro') }}</p>
            <div class="table-scroll">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>{{ t('education.mentor.colStage') }}</th>
                    <th>{{ t('education.mentor.colRole') }}</th>
                    <th>{{ t('education.mentor.colFocus') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, i) in mentorRows" :key="i">
                    <td>{{ row.stage }}</td>
                    <td>{{ row.role }}</td>
                    <td>{{ row.focus }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- 能力徽章 -->
          <div class="content-card" id="badge">
            <h3>{{ t('education.badge.title') }}</h3>
            <p>{{ t('education.badge.intro') }}</p>

            <h4 style="font-size: 17px; color: var(--theme-blue); margin: 20px 0 10px">
              {{ t('education.badge.badgesTitle') }}
            </h4>
            <div class="badge-grid">
              <div v-for="(badge, i) in badges" :key="i" class="badge-item">
                <h4>{{ badge.title }}</h4>
                <p>{{ badge.desc }}</p>
              </div>
            </div>

            <h4 style="font-size: 17px; color: var(--theme-blue); margin: 20px 0 10px">
              {{ t('education.badge.gpaTitle') }}
            </h4>
            <div class="highlight-box">
              <p>
                <strong>{{ t('education.badge.gpaFormula') }}</strong>
              </p>
              <p>{{ t('education.badge.gpaLevels') }}</p>
              <p>{{ t('education.badge.gpaTranscript') }}</p>
            </div>

            <h4 style="font-size: 17px; color: var(--theme-blue); margin: 20px 0 10px">
              {{ t('education.badge.portfolioTitle') }}
            </h4>
            <p style="text-indent: 0">{{ t('education.badge.portfolioBody') }}</p>
          </div>

          <!-- 实践教学体系 -->
          <div class="content-card" id="practice">
            <h3>{{ t('education.practice.title') }}</h3>
            <p>{{ t('education.practice.intro') }}</p>

            <h4 style="font-size: 17px; color: var(--theme-blue); margin: 20px 0 10px">
              {{ t('education.practice.flowTitle') }}
            </h4>
            <div v-for="(step, i) in practiceSteps" :key="i" class="highlight-box">
              <h4>{{ step.title }}</h4>
              <p>{{ step.body }}</p>
            </div>
          </div>
        </div>

        <aside class="content-sidebar">
          <div class="sidebar-nav">
            <h4>{{ t('education.sidebar.navTitle') }}</h4>
            <ul>
              <li>
                <a href="#pbl">{{ t('education.sidebar.navPbl') }}</a>
              </li>
              <li>
                <a href="#projects">{{ t('education.sidebar.navProjects') }}</a>
              </li>
              <li>
                <a href="#mentor">{{ t('education.sidebar.navMentor') }}</a>
              </li>
              <li>
                <a href="#badge">{{ t('education.sidebar.navBadge') }}</a>
              </li>
              <li>
                <a href="#practice">{{ t('education.sidebar.navPractice') }}</a>
              </li>
            </ul>
          </div>
          <div class="sidebar-nav">
            <h4>{{ t('education.sidebar.highlightsTitle') }}</h4>
            <p style="font-size: 14px; color: var(--text-secondary); margin-bottom: 12px">
              <strong>{{ t('education.sidebar.highlight1Title') }}</strong>
              <br />
              {{ t('education.sidebar.highlight1Body') }}
            </p>
            <p style="font-size: 14px; color: var(--text-secondary); margin-bottom: 12px">
              <strong>{{ t('education.sidebar.highlight2Title') }}</strong>
              <br />
              {{ t('education.sidebar.highlight2Body') }}
            </p>
            <!-- <p style="font-size:14px; color:var(--text-secondary); margin-bottom:12px;"> <strong>{{ t('education.sidebar.highlight3Title') }}</strong><br>{{ t('education.sidebar.highlight3Body') }}</p>
              <p style="font-size:14px; color:var(--text-secondary);"> <strong>{{ t('education.sidebar.highlight4Title') }}</strong><br>{{ t('education.sidebar.highlight4Body') }}</p> -->
          </div>
        </aside>
      </div>
    </div>
  </section>
</template>
